import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const PANEL_API = `${BACKEND_URL}/api/panel`;
const TOKEN_KEY = "ariaf_panel_token";

const panelApi = axios.create({ baseURL: PANEL_API });

panelApi.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/**
 * Convert any axios error into a single human-readable Turkish string.
 *
 * FastAPI returns 3 shapes we have to normalise so `toast.error(...)` shows
 * something sensible in every case:
 *   • `{ detail: "..." }`                  → HTTPException (handled controllers)
 *   • `{ detail: [ {msg, loc}, ... ] }`    → pydantic 422 validation
 *   • network errors / offline             → axios `err.message`
 *
 * Falls back to a generic Turkish message if nothing matches.
 */
const FIELD_LABELS_TR = {
  password: "Şifre",
  email: "E-posta",
  name: "Ad Soyad",
  role: "Rol",
  hotel_name: "Otel",
  agency_id: "Acente",
  phone: "Telefon",
};

function _translatePydanticMsg(msg = "") {
  const m = String(msg).trim();
  if (/at least (\d+) characters?/i.test(m)) {
    const n = m.match(/at least (\d+)/i)?.[1] || "6";
    return `en az ${n} karakter olmalı`;
  }
  if (/at most (\d+) characters?/i.test(m)) {
    const n = m.match(/at most (\d+)/i)?.[1] || "";
    return `en fazla ${n} karakter olmalı`;
  }
  if (/valid email/i.test(m) || /email address/i.test(m)) return "geçerli bir e-posta adresi girin";
  if (/field required/i.test(m) || /input should be/i.test(m)) return "zorunlu alan — lütfen doldurun";
  if (/value is not a valid/i.test(m)) return "geçerli bir değer girin";
  if (/greater than or equal/i.test(m)) {
    const n = m.match(/(-?\d+(?:\.\d+)?)/)?.[1] || "0";
    return `${n} veya üstü olmalı`;
  }
  return m;  // fallback: pass through the raw pydantic English
}

export function readableApiError(err, fallback = "Beklenmeyen bir hata oluştu") {
  if (!err) return fallback;
  const d = err?.response?.data?.detail;
  if (typeof d === "string" && d.trim()) return d;
  if (Array.isArray(d) && d.length) {
    const parts = d.map((it) => {
      const rawField = Array.isArray(it?.loc) ? it.loc.slice(-1)[0] : "";
      const field = FIELD_LABELS_TR[rawField] || rawField || "";
      const msg = _translatePydanticMsg(it?.msg || it?.message || "");
      return field ? `${field}: ${msg}` : msg;
    }).filter(Boolean);
    if (parts.length) return parts.join(" · ");
  }
  if (typeof err?.response?.data?.message === "string") return err.response.data.message;
  if (typeof err?.message === "string" && err.message.trim()) return err.message;
  return fallback;
}

panelApi.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
    }
    // Ensure downstream `.catch` code that reads `err.response.data.detail` as
    // a string keeps working — attach a normalised `err.friendly` string so
    // call sites can just do `toast.error(err.friendly)` when they want.
    try {
      err.friendly = readableApiError(err);
    } catch { /* non-fatal */ }
    return Promise.reject(err);
  }
);

export function setPanelToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function getPanelToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export default panelApi;
